import { useEffect, useState } from "react";
import { chartUtils, dateTimeUtils, formatUtils, numericUtils } from "../../utils";
import { DashboardWidget, DashboardWidgetContent, DashboardWidgetHeader } from "../dashboard/widget";
import { mainConfig, Plot } from "../common";
import { SecurityBwicStatistics } from "../../types/security/SecurityBwicStatistics";
import { SubscriptionFeature } from "../../types/billing/SubscriptionFeature";
import { DashboardSkeleton } from "../dashboard/DashboardSkeleton";
import { ChartRequiredFeature } from "../access/ChartRequiredFeature";
import { DashboardNoData } from "../dashboard/DashboardNoData";

const tickColor = '#e4e4e4';
const tickFont = {
    family: 'Roboto, Arial, Helvetica, sans-serif',
    size: 12,
    color: '#7f7f7f',
}
const hoverlabel = {
    bgcolor: '#4a4a4a',
    bordercolor: '#4a4a4a',
    font: {
        color: '#ffffff',
        family: 'Roboto, Arial, Helvetica, sans-serif',
        size: 12
    },
    align: 'left'
}

interface Props {
    targetPositionId: number;
    feature?: SubscriptionFeature;
    history: SecurityBwicStatistics[];
}

export function DmWalChart({ history, feature, targetPositionId }: Props) {
    const [selectedBwic, setSelectedBwic] = useState(history[0]?.referenceName);
    const bwic = history.find(x => x.referenceName === selectedBwic);

    useEffect(() => {
        // reset drop down selection if another bwic position is selected
        setSelectedBwic(history[0]?.referenceName);
    }, [targetPositionId]) // eslint-disable-line react-hooks/exhaustive-deps

    if (!bwic) return null;

    const renderSelect = () =>
        <select
            value={selectedBwic}
            onChange={e => setSelectedBwic(e.target.value)}
            className="form-control form-control-sm form-select"
        >
            {history.map(x => <option key={x.referenceName} value={x.referenceName}>
                {dateTimeUtils.utcToLocalString(x.bidsDueUtc, 'MM/DD/YY')}</option>)}
        </select>

    const getLayout = () => {
        return {
            showlegend: false,
            autosize: true,
            margin: {
                l: 45,
                r: 0,
                t: 10,
                b: 45,
            },
            hovermode: 'closest',
            hoverlabel,
            xaxis: {
                autorange: true,
                tickformat: '.2~f',
                fixedrange: true,
                rangemode: 'nonnegative',
                showgrid: false,
                showline: true,
                ticks: 'outside',
                tickcolor: tickColor,
                tickfont: tickFont,
                linecolor: tickColor
            },
            yaxis: {
                autorange: true,
                zeroline: false,
                fixedrange: true,
                gridcolor: tickColor,
                tickfont: tickFont,
                tickcolor: tickColor,
                tickformat: ',',
            }
        };
    };

    const getChartData = () => {
        const x = bwic.colorOptRedemptions?.filter(x => numericUtils.isNumber(x.wal)).map(x => formatUtils.formatDecimal(+(x.wal!))) ?? [];
        const y = bwic.colorOptRedemptions?.filter(x => numericUtils.isNumber(x.dm)).map(x => numericUtils.numberOrDefault(x.dm)) ?? [];
        const hovertemplate = `WAL: %{x:.2f} <br> DM: %{y:,.3f}<extra></extra>`;

        if (x.length > 1 && y.length > 1) {
            return [{
                x,
                y,
                mode: 'lines',
                type: 'scatter',
                hovertemplate,
            }]
        } else if (numericUtils.isNumber(bwic.colorDm) && (numericUtils.isNumber(bwic.colorWal) || numericUtils.isNumber(bwic.evalPriceTalkWal))) {
            return [{
                x: [numericUtils.numberOrDefault(bwic.colorWal, numericUtils.numberOrDefault(bwic.evalPriceTalkWal))],
                y: [+bwic.colorDm],
                mode: 'markers',
                type: 'scatter',
                hovertemplate
            }]
        } else if ( // Draw point for the single color redemption
            x.length > 0 &&
            y.length > 0 &&
            numericUtils.isNumber(x[0]) &&
            numericUtils.isNumber(y[0])) {
            return [{
                x,
                y,
                mode: 'markers',
                type: 'scatter',
                hovertemplate
            }]
        }

        return [];
    };

    const renderChart = () => {
        const data = getChartData();

        return data.length
            ? <Plot
                onHover={chartUtils.setCursor('pointer')}
                onUnhover={chartUtils.setCursor('crosshair')}
                data={data}
                layout={getLayout()}
                config={mainConfig}
            />
            : <DashboardNoData
                imgName="no-data2"
                imgWidth={164}
                imgHeight={128}
            />;
    }

    const chartName = "DM/WAL Curve";

    return (
        <DashboardWidget title={chartName} className="color-dispersion">
            <DashboardSkeleton>
                <ChartRequiredFeature
                    feature={feature}
                    chartName={chartName}
                    blockedClassName="restricted-placeholder-dm-wal-curve"
                >
                    <DashboardWidgetHeader className="flex-row">
                        <h3 className="display-inline">{chartName}</h3>
                        {renderSelect()}
                    </DashboardWidgetHeader>
                    <DashboardWidgetContent
                        description={getChartData().length ? `Price conversion to DM based on WAL as of ${dateTimeUtils.utcToLocalString(bwic.bidsDueUtc, 'MM/DD/YY')}` : null}
                    >
                        {renderChart()}
                    </DashboardWidgetContent>
                </ChartRequiredFeature>
            </DashboardSkeleton>
        </DashboardWidget>
    );
}